/**
 * FilterPills — renders WHERE / HAVING fragments as a flow of pills. Each pill
 * is a non-editable expression with a leading AND/OR connector when applicable.
 * Per § 1.4, each pill ships click-to-edit text + disabled `×`; the section
 * ships a single disabled `[+ Add]`. (The never-wired per-pill negation glyph
 * was removed — negating is done on a group in the Rail view.)
 *
 * VQE-Edit wiring: when an `editContext` is supplied (and a schema snapshot has
 * loaded), pills are derived from the resolver's `structure.where[]` /
 * `structure.having[]` (the SAME fragment list the SR-Edit applicator
 * indexes), so each pill's index equals the `modify-where`/`modify-having` and
 * `remove-where`/`remove-having` `fragmentIndex` — even under a top-level `OR`
 * or a `BETWEEN`. The pill `×` commits a `removeFilter`, the pill text is
 * click-to-edit (commits `modifyFilter`), and the footer offers a single
 * `[+ Add]` whose picker carries an AND/OR connector toggle (shown once the
 * clause already has a predicate) committing `addFilter` with the chosen
 * combinator — both edit-actions take a `clause` option (`kind`) so the same
 * flat trio serves WHERE and HAVING. Read-only / no-`editContext` falls back
 * to the local `splitTopLevelBoolean` splitter for display only.
 *
 * Subquery wiring (this slice): if the pill text contains an inline
 * `(SELECT … )` group (scalar subquery, or an `IN (SELECT … )` list), the pill
 * splits its rendered content around the paren range and inserts a
 * `<SubquerySection mode='chip'>` in place — collapsed by default with a focus
 * affordance. The IN-list case picks up `prefixLabel='IN'` so the chip reads
 * `[IN] ( SELECT … )` rather than just `( SELECT … )`.
 *
 * Destructive guard (prim #5, Advisor 2 §A3 / DML-20): `UpdateRenderer` /
 * `DeleteRenderer` / `CompactUpdateSections` / `CompactDeleteSections` pass a
 * `destructiveGuard` prop naming the statement's verb + resolved target text.
 * When set, the `×` on the LAST remaining WHERE fragment, or on a
 * "correlation" pill (one whose predicate references BOTH the DML target and
 * a FROM/USING relation — the join/correlation condition an `UPDATE … FROM` /
 * `DELETE … USING` depends on), routes through `ConfirmDestructiveDialog`
 * instead of committing directly. SELECT/HAVING mounts never pass this prop,
 * so their pills are unaffected. DML mounts never pass `enableViewModes`
 * (see the gate below), so only the flat-pill path needs guarding — the
 * Rail/group paths provably don't mount for a guarded instance. The dialog's
 * COPY (not its trigger) branches by which condition fired — the last-
 * remaining-fragment case keeps the "every row" body (`removeLastDmlFilter`),
 * while a correlation pill removed with OTHER filters still standing gets the
 * accurate join-hazard body (`removeCorrelationFilter`) instead, since "no
 * WHERE" would be false there (review fix, see `vqeCopy.confirm`).
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link2 } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { extractStructureForContext, toCommitCtx } from '../../edit/editContext';
import { clearFilters } from '../../edit/edits/clearFilters';
import { removeFilter } from '../../edit/edits/removeFilter';
import { foldWholeGroups } from '../../../../sql-resolver/edit/wholePredicate';
import { isWholeChipFilter } from '../common/looseFkEditEnrichment';
import type { DmlWhereScope } from '../common/dmlWhereScope';
import { inScopeRelationPrefixes, shouldFlagOutOfScope } from '../common/inScopeRelations';
import { RemoveButton } from '../common/RemoveButton';
import { VqeTooltip } from '../common/VqeTooltip';
import { ClauseCard } from './ClauseCard';
import { ConfirmDestructiveDialog } from '../../ux/ConfirmDestructiveDialog';
import { FilterAddBuilder } from './FilterAddBuilder';
import { FilterEditablePill } from './FilterEditablePill';
import { FilterPillLabels } from './FilterPillLabels';
import { renderInlineSubqueries } from './renderInlineSubqueries';
import type { SubqueryWiring } from './SelectRenderer';
import { splitTopLevelBoolean } from './splitTopLevel';
import type { FromItem, QueryStructure } from '../../../../sql-resolver/types';
import { flattenWhereTreeNodes, parentConnectorOf, whereFragmentsFromStructure } from './whereFragments';
import { InlineWhereGroupPill } from './InlineWhereGroup';
import { LeafConvertAdd } from './LeafConvertAdd';
import { buildDrillLabel, extractPredicateColumn } from '../../ux/drillLabel';
import { vqeCopy } from '../../ux/vqeCopy';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import { useVqeStore } from '../../store/vqeStore';
import type { WhereViewMode } from '../../store/vqeStore';
import { effectiveWhereView, whereLeafCount, whereViewKey } from './whereViewMode';
import { WhereRailView } from './WhereRailView';
import { WhereViewToggle } from './WhereViewToggle';
import { WhereConnectorChip } from './WhereConnectorChip';

export type FilterPillsKind = 'where' | 'having';

/** DML mount's guard input (prim #5) — verb + the already-resolved target
 *  display text (the SAME string `DmlTargetCard`/the compact banner shows). */
export interface FilterPillsDestructiveGuard {
  readonly verb: 'UPDATE' | 'DELETE';
  readonly targetText: string;
  /** `dmlWhereScope` of the statement — words the empty WHERE row's warning. */
  readonly scope?: DmlWhereScope;
}

/** Per-pill guard info computed by `FilterPills` and handed to the leaf
 *  `FilterPillRow` — whether ITS `×` must route through the confirm dialog,
 *  and (DML-20) the other relation's display name when it's a correlation
 *  pill. `null` for every pill of an unguarded (SELECT/HAVING) instance. */
interface PillGuard {
  readonly guarded: boolean;
  readonly correlationOther: string | null;
  readonly destructiveGuard: FilterPillsDestructiveGuard;
  readonly onRequest: () => void;
}

/** A dotted `alias.column` / `table.column` reference's leading identifier,
 *  both raw (for display) and folded (for set-membership matching). */
interface DottedRefPrefix {
  readonly raw: string;
  readonly folded: string;
}

const DOTTED_REF_RE = /([A-Za-z_][\w$]*)\s*\.\s*[A-Za-z_][\w$]*/g;

function referencedDottedPrefixes(expr: string): DottedRefPrefix[] {
  const out: DottedRefPrefix[] = [];
  DOTTED_REF_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = DOTTED_REF_RE.exec(expr))) {
    out.push({ raw: m[1], folded: m[1].toLowerCase() });
  }
  return out;
}

/** Folded relation-name + alias prefixes for a single `FromItem` (the DML
 *  target, e.g. `structure.updateTarget` / `structure.deleteTarget`) — the
 *  single-item analogue of `inScopeRelationPrefixes`. */
function targetRelationPrefixes(target: FromItem | null | undefined): Set<string> {
  const prefixes = new Set<string>();
  if (!target) return prefixes;
  const t = target.target;
  if (t.kind === 'relation') prefixes.add(t.qualified.foldedName.toLowerCase());
  else if (t.kind === 'cte-ref') prefixes.add(t.cte.name.foldedName.toLowerCase());
  if (target.alias?.aliasFolded) prefixes.add(target.alias.aliasFolded.toLowerCase());
  return prefixes;
}

/**
 * DML-20 — a pill "correlates" the target to a FROM/USING relation when its
 * predicate text references BOTH a target prefix AND a distinct in-scope
 * FROM/JOIN/USING prefix. Returns the RAW (on-screen-cased) other-relation
 * reference for the tooltip, or `null` when the predicate isn't a
 * correlation (a plain filter on the target, or on a joined relation alone).
 */
function detectCorrelation(
  exprText: string,
  targetPrefixes: ReadonlySet<string>,
  fromPrefixes: ReadonlySet<string>,
): { readonly other: string } | null {
  if (targetPrefixes.size === 0 || fromPrefixes.size === 0) return null;
  let hasTarget = false;
  let other: string | null = null;
  for (const ref of referencedDottedPrefixes(exprText)) {
    if (targetPrefixes.has(ref.folded)) hasTarget = true;
    else if (!other && fromPrefixes.has(ref.folded)) other = ref.raw;
  }
  return hasTarget && other ? { other } : null;
}

/**
 * COM-34/35 — a drilled (breadcrumb-nested) `<SelectRenderer>` mount receives
 * the ENCLOSING statement's own FROM aliases as `outerAliases` (see
 * `SelectRenderer`'s `outerAliases` prop and `useVqeTabDrill`'s drill
 * wiring). A pill "references the outer scope" when one of its dotted
 * `alias.column` prefixes folds to one of those outer aliases — e.g. the `f`
 * in a drilled `i.film_id = f.film_id` predicate. Returns the RAW (on-screen
 * cased) outer alias for the tooltip, or `null` when the predicate has no
 * outer-scope reference (or `outerAliases` is absent/empty — the un-drilled,
 * byte-unchanged case).
 */
function findOuterAliasMatch(
  exprText: string,
  outerAliases: readonly string[] | undefined,
): string | null {
  if (!outerAliases || outerAliases.length === 0) return null;
  const folded = new Set(outerAliases.map((a) => a.toLowerCase()));
  for (const ref of referencedDottedPrefixes(exprText)) {
    if (folded.has(ref.folded)) return ref.raw;
  }
  return null;
}

// TODO(vqe-copy): Lane A (ANA-13) owns vqeCopy this wave — this tooltip stays
// a local const rather than a new vqeCopy line. Fold into vqeCopy once that
// lane lands.
function outerAliasTooltip(alias: string): string {
  return `References outer alias \`${alias}\` from the enclosing query`;
}

export interface FilterPillsProps {
  readonly bufferText: string;
  readonly span: { readonly start: number; readonly end: number } | null;
  readonly options: ParseOptions;
  readonly kind: FilterPillsKind;
  readonly subqueryCtx?: SubqueryWiring;
  readonly editContext?: VqeEditContext;
  readonly enableViewModes?: boolean;
  /**
   * COM-34/35 — the ENCLOSING statement's own FROM aliases, threaded down by
   * a drilled `<SelectRenderer>` mount so a correlated predicate's outer-scope
   * token (e.g. `f` in a drilled `i.film_id = f.film_id`) can be badged as
   * resolving OUTSIDE this frame. Distinct from `subqueryCtx.outerAliases`,
   * which is THIS level's own aliases for grandchild-correlation detection —
   * do not conflate. Absent/empty ⇒ no badge (byte-unchanged default).
   */
  readonly outerAliases?: readonly string[];
  /**
   * `'compact'` (prim #1 §4) collapses the EMPTY-clause state to a single-line
   * density row instead of the ~180px empty card — adopted by the compact
   * popover's edit sections. Populated clauses are unaffected (they always use
   * the full card). Defaults to `'default'`.
   */
  readonly density?: 'default' | 'compact';
  /**
   * Prim #5 — passed ONLY by the DML mounts (`UpdateRenderer` / `DeleteRenderer`
   * / `CompactUpdateSections` / `CompactDeleteSections`). When set, removing
   * the last remaining fragment, or a correlation pill (DML-20), opens
   * `ConfirmDestructiveDialog` instead of committing directly. With no WHERE
   * at all, the empty WHERE row becomes the red "changes every row" warning.
   */
  readonly destructiveGuard?: FilterPillsDestructiveGuard;
  /**
   * Render nothing for an empty clause — the SELECT's clause add row offers it
   * (`FilterClauseAddButton`). Never set by the DML mounts: their empty WHERE
   * is the red "changes every row" warning.
   */
  readonly hideEmpty?: boolean;
}

const TITLES: Record<FilterPillsKind, string> = {
  where: 'Where',
  having: 'Having',
};

export function FilterPills({
  bufferText,
  span,
  options,
  kind,
  subqueryCtx,
  editContext,
  enableViewModes,
  density = 'default',
  destructiveGuard,
  outerAliases,
  hideEmpty,
}: FilterPillsProps) {
  // Folded aliases + table names in the query's FROM/JOIN — drives the
  // out-of-scope red marking on predicate pills that reference an absent table.
  // Empty when the FROM set can't be resolved, so nothing is flagged then.
  // Hoisted above the `!span` early return to keep hook order stable.
  // Resolve the statement structure ONCE: it feeds both the out-of-scope marking
  // and the editable pill list (so pills come from the SAME fragment list the
  // SR-Edit applicator indexes into). Undefined until a schema snapshot exists.
  const structure = useMemo<QueryStructure | undefined>(() => {
    if (!editContext || !editContext.service.getSchemaSnapshot()) return undefined;
    try {
      return extractStructureForContext(editContext);
    } catch {
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editContext, bufferText]);

  const inScopePrefixes = useMemo(
    () => (structure ? inScopeRelationPrefixes(structure) : new Set<string>()),
    [structure],
  );

  // Prim #5 — the DML target's OWN relation-name + alias prefixes, read
  // directly off the structure's `updateTarget` / `deleteTarget` (a `FromItem`
  // just like a FROM item, but never itself IN `fromItems`). Empty when no
  // guard is active or the statement kind isn't update/delete.
  const targetPrefixes = useMemo(() => {
    if (!destructiveGuard || !structure) return new Set<string>();
    const target =
      structure.kind === 'update'
        ? structure.updateTarget
        : structure.kind === 'delete'
          ? structure.deleteTarget
          : undefined;
    return targetRelationPrefixes(target);
  }, [destructiveGuard, structure]);

  // Prim #5 — index of the fragment awaiting confirmation, or null when no
  // guarded removal is pending. A single dialog instance serves every pill.
  // Report G6 — a pending "Clear all" is represented by the `-1` sentinel
  // (below), which awaits the SAME confirm as removing the last fragment.
  const [pendingRemoveIdx, setPendingRemoveIdx] = useState<number | null>(null);

  // Slice 3B.2 — view-mode hooks. All unconditional (before early return) to
  // keep hook order stable. Gate is applied in computed values below.
  // HAVING parity (Plan 3 Task 5): `clauseTree` reads the kind-appropriate tree
  // so BOTH the view-mode toggle/Rail branch and the inline explicit-group
  // decomposition serve WHERE and HAVING from the same gates. `viewKey` is
  // re-keyed by `kind` (Plan 3 decision 2) so WHERE and HAVING pin their
  // Auto/Inline/Rail choice independently — the vqeStore slice is transient
  // (never persisted to disk), so widening the key is free.
  const { track } = useAnalytics();
  const setWhereViewMode = useVqeStore((s) => s.setWhereViewMode);
  const viewKey =
    editContext ? whereViewKey(editContext.documentId, editContext.statementId, kind) : '';
  const pinnedMode = useVqeStore((s) => s.whereViewMode[viewKey] ?? 'auto');
  const rawTree = kind === 'having' ? structure?.havingTree : structure?.whereTree;
  // A parenthesised group the chip reopens whole (a multi-part filter it
  // wrote: JSON `!=`, a JSON test behind its validity guard) is ONE condition
  // — for the view choice, the Inline pills and the Rail alike.
  const clauseTree =
    rawTree && editContext
      ? foldWholeGroups(rawTree, bufferText, (text) => isWholeChipFilter(text, editContext))
      : rawTree;
  // Only offer the Inline/Rail layout toggle when there is something to lay out —
  // a clause with ≥2 predicates joined by AND/OR. With 0 or 1 leaf the toggle is
  // a no-op (effectiveWhereView already resolves to 'inline'), so we hide it.
  const viewModesOn = !!enableViewModes && !!clauseTree && whereLeafCount(clauseTree) >= 2;
  const effectiveView = viewModesOn && clauseTree ? effectiveWhereView(pinnedMode, clauseTree) : 'inline';
  const onPickViewMode = (mode: WhereViewMode): void => {
    if (mode === pinnedMode) return;
    setWhereViewMode(viewKey, mode);
    track(`feature:vqe_${kind}_view_mode`, { mode });
  };

  if (!span) {
    // Empty WHERE / HAVING — render the section only when an `editContext` is
    // available so users can synthesize a fresh clause via [+ add filter].
    // HAVING additionally requires the statement to already have a GROUP BY —
    // a bare `SELECT … FROM t` doesn't invite a HAVING clause (Plan 3 decision 1).
    // An UPDATE / DELETE (the guarded mounts) always shows its empty WHERE,
    // tinted red and saying which rows change, even before editing is ready.
    if (!editContext && !destructiveGuard) return null;
    if (hideEmpty && !destructiveGuard) return null;
    if (kind === 'having' && !structure?.groupBy?.length) return null;
    const warning = destructiveGuard
      ? vqeCopy.dmlScope.emptyWhere(
          destructiveGuard.verb,
          destructiveGuard.targetText,
          destructiveGuard.scope ?? 'all-rows',
        )
      : undefined;
    return <EmptyFilterSection editContext={editContext} kind={kind} density={density} warning={warning} />;
  }
  const testIdRoot = `vqe-${kind}`;

  const editReady = !!editContext && !!editContext.service.getSchemaSnapshot();

  // Editable pills come from the resolver's structure.where[] / structure.having[]
  // — the SAME list the applicator indexes — so pill index === modify/remove
  // fragmentIndex even under a top-level OR or BETWEEN. Read-only (no
  // editContext / no snapshot) falls back to the local AND+OR splitter.
  const clauseFragmentSource = kind === 'having' ? structure?.having : structure?.where;
  const editableFragments =
    editReady && clauseFragmentSource ? whereFragmentsFromStructure(clauseFragmentSource) : null;
  const fragments =
    editableFragments ?? splitTopLevelBoolean(bufferText, span.start, span.end, options);
  const canIndexEdit = editableFragments != null;

  // Inline-view per-slot tree nodes, index-aligned with `fragments` (the
  // kind-appropriate `clauseTree`). Drives two things:
  //  • F4 — an explicit parenthesised group renders as its own tinted box of
  //    separate sub-pills (not one opaque clumped pill).
  //  • Round 8 (B) — each standalone leaf gets its tree PATH so its trailing
  //    "+ or" / "+ and" chip can convert it to a group via `group-leaf`.
  // Leaves still use the flat fragment-indexed FilterPillRow, so the pill-index
  // edit contract is unchanged. Null (→ plain flat list, no per-leaf add) when
  // we're in the Rail view or the node walk and the fragment list ever
  // disagree in length.
  //
  // Gated on the `enableViewModes` PROP (not the computed `viewModesOn`, which
  // also needs ≥2 leaves): only the SELECT/Compact WHERE and HAVING mounts opt
  // in (both pass `enableViewModes` — Plan 3 Task 5), so DELETE / UPDATE WHERE
  // (which pass `editContext` but never `enableViewModes`) keep their plain
  // flat-pill rendering — yet a single-leaf SELECT WHERE/HAVING still gets the
  // per-leaf convert-to-group chip.
  const renderingInline = !(viewModesOn && effectiveView === 'rail' && clauseTree);
  const emittedNodes =
    !!enableViewModes && renderingInline && clauseTree
      ? (() => {
          const nodes = flattenWhereTreeNodes(clauseTree);
          return nodes.length === fragments.length ? nodes : null;
        })()
      : null;

  // The "+ or" / "+ and" chips show only on the hovered or focused condition
  // (ConditionChipSlot), so a WHERE carries no chip per condition at rest.

  // No reserved leading "AND / OR" column (the old DML-32 slot): the first
  // condition starts flush in every WHERE / HAVING, SELECT and DML alike
  // (owner ruling 2026-09-26). Wrapped rows start with their AND / OR chip.

  // `FilterPillsKind` and `BooleanClauseTarget` are the same `'where' |
  // 'having'` union, so `{ clause: kind }` targets the right clause on both
  // `removeFilter` and `clearFilters` without a branch — `removeFilter`'s
  // default is `'where'` anyway, so passing it explicitly is a no-op there.
  const clauseOpts = { clause: kind };

  // Prim #5 — settle the pending guarded removal. Confirm runs the SAME
  // `removeFilter` / `clearFilters` commit the unguarded path would have run
  // directly; cancel just closes. Mirrors the CteCard / FromItemRow
  // destructive-confirm analytics convention
  // (`feature:vqe_destructive_confirm_apply/cancel`).
  const onConfirmGuardedRemove = (): void => {
    const idx = pendingRemoveIdx;
    setPendingRemoveIdx(null);
    if (idx === null || !editContext) return;
    track('feature:vqe_destructive_confirm_apply', { kind: 'dml-filter' });
    if (idx === -1) void clearFilters(toCommitCtx(editContext), clauseOpts);
    else void removeFilter(idx, toCommitCtx(editContext), clauseOpts);
  };
  const onCancelGuardedRemove = (): void => {
    setPendingRemoveIdx(null);
    track('feature:vqe_destructive_confirm_cancel', { kind: 'dml-filter' });
  };

  // Report G6 — "Clear all" either commits directly (unguarded SELECT/HAVING)
  // or, on a guarded UPDATE/DELETE WHERE, opens the SAME confirm dialog as
  // removing the last condition — clearing the whole clause always leaves
  // the statement with no WHERE, so the premise is identical.
  const onClearAll = (): void => {
    if (!editContext) return;
    if (destructiveGuard) {
      setPendingRemoveIdx(-1);
      return;
    }
    void clearFilters(toCommitCtx(editContext), clauseOpts);
  };

  // Review fix (prim #5) — select the confirm dialog's COPY (not its
  // trigger, which is untouched above) by which of the two guard conditions
  // fired for the PENDING removal. `fragments.length === 1` (or the
  // `-1` "Clear all" sentinel) is the "last remaining filter" case — the "every
  // row" premise holds since nothing else remains. A guarded per-pill removal
  // with `fragments.length > 1` can ONLY reach here via the correlation half
  // of `pillGuard.guarded`'s OR above, so there the accurate premise is the
  // join hazard, not "no WHERE" (a real filter still remains). `pendingCorrelation`
  // recomputes the SAME correlation the pill itself detected, so its `.other`
  // matches the pill's own tooltip.
  const pendingIsLastFilter = pendingRemoveIdx === -1 || fragments.length === 1;
  // `fragments[-1]` is `undefined` in JS, so the -1 "Clear all" sentinel
  // falls out of this naturally — no extra range check needed.
  const pendingFrag = pendingRemoveIdx !== null ? fragments[pendingRemoveIdx] : undefined;
  const pendingCorrelation =
    destructiveGuard && !pendingIsLastFilter && pendingFrag
      ? detectCorrelation(pendingFrag.text, targetPrefixes, inScopePrefixes)
      : null;

  return (
    <FilterPillLabels editContext={editContext}>
    <ClauseCard
      title={TITLES[kind]}
      testId={`${testIdRoot}-section`}
      // One line per clause: title left, pills + "+ Add condition" wrapping
      // beside it, the Auto / Inline / Rail switch (two or more conditions) at
      // the right end. The Rail view is one condition per line by design, so it
      // keeps the stacked card (title + switch above a full-width tree).
      density={viewModesOn && effectiveView === 'rail' && clauseTree ? 'default' : 'line'}
      accessory={
        viewModesOn ? (
          <WhereViewToggle kind={kind} value={pinnedMode} onChange={onPickViewMode} />
        ) : undefined
      }
      footer={
        <FilterPillsFooter
          editContext={editContext}
          kind={kind}
          editReady={editReady}
          testIdRoot={testIdRoot}
          hasFragments={fragments.length > 0}
          onClearAll={editReady && fragments.length >= 2 ? onClearAll : undefined}
        />
      }
    >
      {viewModesOn && effectiveView === 'rail' && clauseTree ? (
        <WhereRailView
          root={clauseTree}
          kind={kind}
          editContext={canIndexEdit ? editContext : undefined}
          subquery={subqueryCtx ? { subqueryCtx, bufferText, options } : undefined}
        />
      ) : fragments.length === 0 ? (
        <p className="text-xs italic text-muted-foreground" data-testid={`${testIdRoot}-empty`}>
          No predicates parsed.
        </p>
      ) : (
        <ul className="flex flex-wrap items-center gap-2" data-testid={`${testIdRoot}-pills`}>
          {fragments.map((frag, idx) => {
            // Prim #5 — per-pill guard: DML mounts never set `enableViewModes`
            // (see `emittedNodes` above), so `emitted` is always null here and
            // every guarded pill takes the leaf `FilterPillRow` branch below.
            const correlation = destructiveGuard
              ? detectCorrelation(frag.text, targetPrefixes, inScopePrefixes)
              : null;
            const pillGuard: PillGuard | null = destructiveGuard
              ? {
                  guarded: fragments.length === 1 || !!correlation,
                  correlationOther: correlation?.other ?? null,
                  destructiveGuard,
                  onRequest: () => setPendingRemoveIdx(idx),
                }
              : null;

            // Explicit-group slot (F4): render the tinted, separated sub-pill box.
            const emitted = emittedNodes ? emittedNodes[idx] : null;
            if (emitted && emitted.node.kind === 'group') {
              const groupNode = emitted.node;
              return (
                <InlineWhereGroupPill
                  key={`group-${idx}`}
                  node={groupNode}
                  path={emitted.path}
                  leadingConnector={frag.leadingConnector}
                  testId={`${testIdRoot}-pill-${idx}`}
                  editContext={canIndexEdit ? editContext : undefined}
                  subquery={
                    subqueryCtx ? { subqueryCtx, bufferText, options } : undefined
                  }
                  kind={kind}
                />
              );
            }
            // Leaf slot: the unchanged flat fragment-indexed pill, plus (Round 8
            // B) a trailing "+ or" / "+ and" chip that converts this standalone
            // leaf into a parenthesised group. The chip is gated on editability
            // + a resolved tree path; without `emitted` (read-only / length
            // mismatch) only the pill renders, exactly as before.
            return (
              <FilterPillRow
                key={`${frag.text}-${idx}`}
                frag={frag}
                idx={idx}
                testIdRoot={testIdRoot}
                kind={kind}
                bufferText={bufferText}
                options={options}
                subqueryCtx={subqueryCtx}
                editContext={canIndexEdit ? editContext : undefined}
                outOfScope={shouldFlagOutOfScope(frag.text, inScopePrefixes)}
                pillGuard={pillGuard}
                outerAliases={outerAliases}
                // The connector before this leaf belongs to the leaf's parent
                // group — known only when the tree mapping resolved.
                connectorGroupPath={emitted ? emitted.path.slice(0, -1) : undefined}
                trailing={
                  canIndexEdit && emitted && editContext ? (
                    <LeafConvertAdd
                      path={emitted.path}
                      editContext={editContext}
                      kind={kind}
                      listConnector={parentConnectorOf(clauseTree, emitted.path)}
                    />
                  ) : undefined
                }
              />
            );
          })}
        </ul>
      )}
    </ClauseCard>
    {destructiveGuard ? (
      <ConfirmDestructiveDialog
        open={pendingRemoveIdx !== null}
        testId={`${testIdRoot}-remove-confirm`}
        title={
          pendingIsLastFilter
            ? vqeCopy.confirm.removeLastDmlFilter.title
            : vqeCopy.confirm.removeCorrelationFilter.title
        }
        description={
          pendingIsLastFilter
            ? vqeCopy.confirm.removeLastDmlFilter.body(
                destructiveGuard.verb,
                destructiveGuard.targetText,
              )
            : vqeCopy.confirm.removeCorrelationFilter.body(
                destructiveGuard.targetText,
                pendingCorrelation?.other ?? '',
                destructiveGuard.verb,
              )
        }
        confirmLabel={
          pendingIsLastFilter
            ? vqeCopy.confirm.removeLastDmlFilter.apply
            : vqeCopy.confirm.removeCorrelationFilter.apply
        }
        cancelLabel={
          pendingIsLastFilter
            ? vqeCopy.confirm.removeLastDmlFilter.cancel
            : vqeCopy.confirm.removeCorrelationFilter.cancel
        }
        onConfirm={onConfirmGuardedRemove}
        onCancel={onCancelGuardedRemove}
      />
    ) : null}
    </FilterPillLabels>
  );
}

interface FilterPillRowProps {
  readonly frag: ReturnType<typeof splitTopLevelBoolean>[number];
  readonly idx: number;
  readonly testIdRoot: string;
  readonly kind: FilterPillsKind;
  readonly bufferText: string;
  readonly options: ParseOptions;
  readonly subqueryCtx?: SubqueryWiring;
  readonly editContext?: VqeEditContext;
  readonly outOfScope?: boolean;
  /** Prim #5 — non-null only for a DML (UPDATE/DELETE) WHERE mount. */
  readonly pillGuard?: PillGuard | null;
  /** COM-34/35 — see `FilterPillsProps.outerAliases`. */
  readonly outerAliases?: readonly string[];
  /** Owning group of the leading connector — makes the AND/OR chip a dropdown. */
  readonly connectorGroupPath?: readonly number[];
  /** The condition's "+ or" / "+ and" chip (LeafConvertAdd), after the pill. */
  readonly trailing?: ReactNode;
}

const MUTED_CONNECTOR_CHIP =
  'rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground';

function FilterPillRow({
  frag,
  idx,
  testIdRoot,
  kind,
  bufferText,
  options,
  subqueryCtx,
  editContext,
  outOfScope = false,
  pillGuard = null,
  outerAliases,
  connectorGroupPath,
  trailing,
}: FilterPillRowProps) {
  // WHERE/HAVING drill label uses the predicate's leading column ref; falls
  // back to the positional "WHERE #N" / "HAVING #N" when the fragment opens
  // with a paren group or a non-identifier token.
  const pillDrillLabel = buildDrillLabel({
    kind,
    identifier: extractPredicateColumn(frag.text),
    positionalIndex: idx,
  });
  // A fragment that is one chip filter as a whole stays the editable pill —
  // never subquery drill chips over the chip's own lateral / EXISTS SQL.
  const inlineRender =
    subqueryCtx && !(editContext && isWholeChipFilter(frag.text, editContext))
    ? renderInlineSubqueries({
        bufferText,
        start: frag.start,
        end: frag.end,
        options,
        subqueryCtx,
        testIdPrefix: `${testIdRoot}-pill-${idx}`,
        drillLabelPrefix: pillDrillLabel,
        editContext,
      })
    : { nodes: null, subqueryCount: 0 };
  const removeTooltip = editContext ? 'Remove filter' : undefined;
  // COM-34/35 — pure display, regex-over-text (no resolver/schema data): does
  // this pill's text reference one of the drilled frame's OUTER-scope aliases?
  const outerAliasMatch = findOuterAliasMatch(frag.text, outerAliases);
  return (
    <li
      className="relative flex items-center gap-1.5"
      data-testid={`${testIdRoot}-pill-${idx}`}
      data-has-subquery={inlineRender.subqueryCount > 0 ? 'true' : 'false'}
      data-out-of-scope={outOfScope ? 'true' : 'false'}
    >
      {frag.leadingConnector ? (
        <WhereConnectorChip
          connector={frag.leadingConnector}
          testId={`${testIdRoot}-pill-${idx}-connector`}
          className={MUTED_CONNECTOR_CHIP}
          kind={kind}
          groupPath={connectorGroupPath}
          editContext={editContext}
        />
      ) : null}
      <span
        className={
          outOfScope
            ? 'relative flex min-w-0 flex-wrap items-center gap-1.5 rounded-md border border-red-500/60 bg-red-500/10 px-2.5 py-1 pr-7'
            : 'relative flex min-w-0 flex-wrap items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2.5 py-1 pr-7'
        }
      >
        {inlineRender.subqueryCount > 0 ? (
          <span
            className="flex flex-wrap items-center gap-1"
            data-testid={`${testIdRoot}-pill-${idx}-text`}
          >
            {inlineRender.nodes}
          </span>
        ) : editContext ? (
          <FilterEditablePill
            testId={`${testIdRoot}-pill-${idx}-text`}
            text={frag.text}
            fragmentIndex={idx}
            editContext={editContext}
            clause={kind}
          />
        ) : (
          <span
            className="whitespace-pre-wrap break-words font-mono text-xs"
            data-testid={`${testIdRoot}-pill-${idx}-text`}
          >
            {frag.text}
          </span>
        )}
        {outOfScope ? (
          <VqeTooltip
            selfProvider
            content="This predicate references a table that is not in FROM/JOIN. Add the table or remove the filter."
          >
            <span
              className="shrink-0 cursor-help rounded bg-red-500/15 px-1 py-0 text-[9px] font-semibold uppercase tracking-wide text-red-600 dark:text-red-400"
              data-testid={`${testIdRoot}-pill-${idx}-out-of-scope`}
            >
              unknown table
            </span>
          </VqeTooltip>
        ) : null}
        {outerAliasMatch ? (
          <VqeTooltip selfProvider content={outerAliasTooltip(outerAliasMatch)}>
            <span
              className="shrink-0 cursor-help text-muted-foreground"
              data-testid={`${testIdRoot}-pill-${idx}-outer-alias`}
            >
              <Link2 className="h-3 w-3" aria-hidden="true" />
            </span>
          </VqeTooltip>
        ) : null}
        {pillGuard?.correlationOther ? (
          <VqeTooltip
            selfProvider
            content={vqeCopy.dmlCorrelation.tooltip(
              pillGuard.correlationOther,
              pillGuard.destructiveGuard.targetText,
              pillGuard.destructiveGuard.verb === 'DELETE' ? 'delete' : 'update',
            )}
          >
            <span
              className="shrink-0 cursor-help text-muted-foreground"
              data-testid={`${testIdRoot}-pill-${idx}-correlation`}
            >
              <Link2 className="h-3 w-3" aria-hidden="true" />
            </span>
          </VqeTooltip>
        ) : null}
        <span className="absolute right-1 top-1">
          <RemoveButton
            testId={`${testIdRoot}-pill-${idx}-remove`}
            ariaLabel="Remove filter"
            disabled={!editContext}
            tooltip={removeTooltip}
            onClick={
              editContext
                ? () => {
                    // Prim #5 — a guarded pill routes through the parent's
                    // shared confirm dialog instead of committing directly.
                    if (pillGuard?.guarded) {
                      pillGuard.onRequest();
                      return;
                    }
                    if (kind === 'having') {
                      void removeFilter(idx, toCommitCtx(editContext), { clause: 'having' });
                    } else {
                      void removeFilter(idx, toCommitCtx(editContext));
                    }
                  }
                : undefined
            }
          />
        </span>
      </span>
      {trailing}
    </li>
  );
}

interface FilterPillsFooterProps {
  readonly editContext?: VqeEditContext;
  readonly kind: FilterPillsKind;
  readonly editReady: boolean;
  readonly testIdRoot: string;
  readonly hasFragments: boolean;
  readonly onDraftOpenChange?: (open: boolean) => void;
  readonly triggerLabel?: string;
  /**
   * Report G6 — "Clear all" removes the whole clause in one edit. Set only
   * once the clause has two or more conditions — a query-changing action, so
   * it sits beside `[+ Add condition]` rather than with the Auto/Inline/Rail
   * view controls. Only the populated-clause footer (the main `FilterPills`
   * render) ever passes this — the empty-clause paths (`FilterClauseAddButton`,
   * `EmptyFilterSection`) have nothing to clear.
   */
  readonly onClearAll?: () => void;
}

/**
 * The first condition of an EMPTY WHERE / HAVING, from the SELECT's clause add
 * row: the same builder as the clause's own "+ Add condition", under a short
 * label ("Where" / "Having"). Its draft opens in place in that row; once
 * applied the clause renders in its SQL position.
 */
export function FilterClauseAddButton({
  editContext,
  kind,
  label,
}: {
  readonly editContext: VqeEditContext;
  readonly kind: FilterPillsKind;
  readonly label: string;
}) {
  return (
    <FilterPillsFooter
      editContext={editContext}
      kind={kind}
      editReady={!!editContext.service.getSchemaSnapshot()}
      testIdRoot={`vqe-${kind}`}
      hasFragments={false}
      triggerLabel={label}
    />
  );
}

function FilterPillsFooter({
  editContext,
  kind,
  editReady,
  testIdRoot,
  hasFragments,
  onDraftOpenChange,
  triggerLabel = vqeCopy.actions.addFilterShort,
  onClearAll,
}: FilterPillsFooterProps) {
  // Single "+ Add" (Round 8): the AND/OR choice moved into the picker header as
  // a connector toggle, shown only once the clause already has a predicate (the
  // first predicate needs no connector). Replaces the old Add AND / Add OR pair.
  // Disabled-when-not-ready keeps WHERE's exact tooltip behavior for both
  // kinds — FilterAddBuilder falls back to `vqeCopy.disabled.editingUnavailable`.
  return (
    <div className="flex flex-wrap items-center gap-2">
      <FilterAddBuilder
        testId={`${testIdRoot}-add-filter`}
        triggerLabel={triggerLabel}
        editContext={editReady ? editContext : undefined}
        disabled={!editReady}
        combinator="AND"
        pickConnector={hasFragments}
        clause={kind}
        onDraftOpenChange={onDraftOpenChange}
      />
      {onClearAll && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-1.5 text-xs text-muted-foreground"
          data-testid={`${testIdRoot}-clear`}
          onClick={onClearAll}
        >
          {vqeCopy.actions.clearAll}
        </Button>
      )}
    </div>
  );
}

interface EmptyFilterSectionProps {
  readonly editContext?: VqeEditContext;
  readonly kind: FilterPillsKind;
  readonly density?: 'default' | 'compact';
  /** UPDATE / DELETE with no WHERE: red row whose body says which rows change. */
  readonly warning?: string;
}

// TODO(vqe-copy): consolidate the compact empty-clause label into vqeCopy
// (lane e owns vqeCopy this batch — do not edit it here).
const COMPACT_EMPTY_LABEL = '— none';

function EmptyFilterSection({ editContext, kind, density = 'default', warning }: EmptyFilterSectionProps) {
  const testIdRoot = `vqe-${kind}`;
  const editReady = !!editContext?.service.getSchemaSnapshot();
  const compact = density === 'compact';
  // The "No filters yet" line is about the empty clause; once a draft chip is
  // open it contradicts what's on screen, so it steps aside.
  const [draftOpen, setDraftOpen] = useState(false);
  return (
    <ClauseCard
      title={TITLES[kind]}
      testId={`${testIdRoot}-section`}
      density={density}
      tone={warning ? 'destructive' : 'default'}
      footer={
        <FilterPillsFooter
          editContext={editContext}
          kind={kind}
          editReady={editReady}
          testIdRoot={testIdRoot}
          hasFragments={false}
          onDraftOpenChange={setDraftOpen}
        />
      }
    >
      {draftOpen ? null : (
        <p
          className={warning ? 'text-xs font-medium text-destructive dark:text-red-400' : 'text-xs italic text-muted-foreground'}
          data-testid={`${testIdRoot}-empty`}
        >
          {warning ?? (compact ? COMPACT_EMPTY_LABEL : vqeCopy.empty.noFilters)}
        </p>
      )}
    </ClauseCard>
  );
}
